import React, { ReactNode } from "react";

import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";

import { ExternalLink } from "src/base-components/ExternalLink";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export type DependencyItem = {
  id: string;
  name: string;
  group: string;
  href: string;
  statusPill?: ReactNode;
};

type DependencyListProps = {
  items: DependencyItem[];
  ungrouped?: boolean;
  maxItemsPerGroup?: number;
  dataLoc?: string;
};

export const DEFAULT_MAX_ITEMS_PER_GROUP = 5;

export const DependencyList: React.FC<DependencyListProps> = ({
  items,
  ungrouped = false,
  maxItemsPerGroup = DEFAULT_MAX_ITEMS_PER_GROUP,
  dataLoc,
}) => {
  const grouped = Map.groupBy(items, (item) => item.group);

  if (items.length === 0) {
    return null;
  }

  return (
    <div data-loc={dataLoc}>
      {[...grouped.entries()].map(([group, groupItems]) => {
        const visible = groupItems.slice(0, maxItemsPerGroup);
        const overflowCount = groupItems.length - visible.length;

        return (
          <ul key={group} className="[&:not(:last-child)]:pb-3">
            {!ungrouped && (
              <li
                className="font-inter-semibold-13px text-gray-800"
                data-loc="dependency-list-group-header"
              >
                {group}
              </li>
            )}
            {visible.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-x-2 border-b border-b-transparent py-2 [&:not(:last-child)]:border-gray-200"
                data-loc="dependency-list-item"
              >
                <Tooltip title={item.name} overflowOnly asChild>
                  <span className="mr-auto min-w-0 truncate font-inter-normal-13px text-gray-500">
                    {item.name}
                  </span>
                </Tooltip>
                {item.statusPill}
                <Tooltip placement="bottom-end" title="Open in new tab" asChild>
                  <ExternalLink href={item.href}>
                    <Icon icon={faUpRightFromSquare} size="xs" />
                  </ExternalLink>
                </Tooltip>
              </li>
            ))}
            {overflowCount > 0 && (
              <li
                className="py-2 font-inter-normal-12px text-gray-500"
                data-loc="dependency-list-overflow"
              >
                ... and {overflowCount} more
              </li>
            )}
          </ul>
        );
      })}
    </div>
  );
};
